<template>
  <section class="self-service-provider-page" id="self-service-provider-page">
    <div class="bg-shape bg-shape-one"></div>
    <div class="bg-shape bg-shape-two"></div>

    <main class="admin-main auth-main">
      <div class="auth-shell">
        <header class="auth-hero">
          <p class="eyebrow">Secure Access</p>
          <h1>
            <i class="fe fe-shield m-r-10"></i>
            Provider Self Service
          </h1>
          <p class="subtitle">Select an activity to continue. Verify code is active now.</p>
        </header>

        <section>
          <div class="container px-0">
            <div class="row justify-content-center">
              <div class="col-lg-10">
                <div class="card auth-card m-b-30">
                  <div class="card-body">
                    <div class="section-heading">
                      <h2>Available Activities</h2>
                      <p>Choose the service you need.</p>
                    </div>

                    <div class="activity-grid m-b-30">
                      <button
                        type="button"
                        class="activity-card activity-card--request is-coming-soon"
                        disabled
                      >
                        <span class="activity-card__title">Verify Code</span>
                        <span class="activity-card__text">Verify a referral code for authenticity.</span>
                        <span class="activity-card__badge">Coming soon</span>
                      </button>

                      <button
                        type="button"
                        class="activity-card activity-card--enrollee is-coming-soon"
                        disabled
                      >
                        <span class="activity-card__title">Verify enrollee</span>
                        <span class="activity-card__text">Validate enrollee details before service.</span>
                        <span class="activity-card__badge">Coming soon</span>
                      </button>

                      <button
                        type="button"
                        class="activity-card activity-card--verify"
                        @click="openVerifyCodeForm"
                      >
                        <span class="activity-card__title">Request Code</span>
                        <span class="activity-card__text">Generate and post the referral USSD verification string.</span>
                      </button>
                    </div>

                    <div v-if="!showVerifyCode" class="placeholder-panel">
                      <i class="fe fe-clock m-r-10"></i>
                      Select an activity to continue.
                    </div>

                    <div v-else ref="verifyCodeForm">
                      <div class="row">
                        <div class="col-md-6">
                          <div class="form-group">
                            <label>User Phone Number</label>
                            <input v-phone
                              type="text"
                              class="form-control"
                              v-model.trim="providerVerify.userPhoneNumber"
                              placeholder="e.g. 08012345678"
                              :disabled="loading"
                            />
                          </div>
                        </div>

                        <div class="col-md-6">
                          <div class="form-group">
                            <label>Enrollee Phone Number/ Insurance ID</label>
                            <input v-phone
                              type="text"
                              class="form-control"
                              v-model.trim="providerVerify.enrolleePhoneNumber"
                              placeholder="e.g. 08098765432"
                              :disabled="loading"
                            />
                          </div>
                        </div>

                        <div class="col-md-12">
                          <div class="form-group">
                            <label>Select Facility</label>
                            <v-select
                              v-model="providerVerify.facility"
                              :options="providers"
                              label="agency_name"
                              :disabled="loading || isFetchingProviders"
                              placeholder="Select secondary facility"
                            />
                            <small class="text-muted d-block m-t-10" v-if="isFetchingProviders">
                              Loading facilities...
                            </small>
                            <small class="text-muted d-block m-t-10" v-else-if="!providers.length">
                              No facilities are loaded yet. The selector uses the current agency context.
                            </small>
                          </div>
                        </div>
                      </div>

                      <div class="ussd-preview m-b-20">
                        <p class="ussd-preview__label">Generated USSD string</p>
                        <code>{{ providerVerifyUssdString || 'Waiting for form values...' }}</code>
                        <button
                          type="button"
                          class="btn btn-outline-info btn-sm m-t-10"
                          @click="copyUssdString"
                          :disabled="!providerVerifyUssdString"
                        >
                          <i class="fe fe-copy m-r-5"></i>
                          Copy USSD String
                        </button>
                      </div>

                      <div class="ussd-guide m-b-20">
                        <div class="ussd-guide__item">
                          <strong>P</strong>
                          <span>Enrollee type: Principal</span>
                        </div>
                        <div class="ussd-guide__item">
                          <strong>{{ providerVerify.enrolleePhoneNumber || 'Enrollee number' }}</strong>
                          <span>Enrollee ID or phone number</span>
                        </div>
                        <div class="ussd-guide__item">
                          <strong>{{ facilityIdentifier || 'Facility ID' }}</strong>
                          <span>Secondary facility ID</span>
                        </div>
                        <div class="ussd-guide__item">
                          <strong>R</strong>
                          <span>Referral activity</span>
                        </div>
                      </div>

                      <button
                        type="button"
                        class="btn auth-btn"
                        @click="submitProviderVerifyCode"
                        :disabled="loading || !canSubmitProviderVerify"
                      >
                        <i class="fe fe-send m-r-5"></i>
                        Verify Code
                      </button>

                      <div class="response-panel m-t-20" v-if="providerVerifyResponse">
                        <p class="response-panel__label">USSD response</p>
                        <pre>{{ providerVerifyResponse }}</pre>
                      </div>

                      <p class="text-danger m-b-0" v-if="errorMessage">{{ errorMessage }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div class="vld-parent">
          <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
        </div>
      </div>
    </main>
  </section>
</template>

<script>
  let agency = JSON.parse(localStorage.getItem("agency"));

  import Loading from "vue-loading-overlay";
  import "vue-loading-overlay/dist/vue-loading.css";

  export default {
    components: {
      Loading,
    },
    data() {
      return {
        showVerifyCode: false,
        loading: false,
        errorMessage: "",
        isLoading: false,
        fullPage: true,
        providers: [],
        isFetchingProviders: false,
        providerVerify: {
          userPhoneNumber: "",
          enrolleePhoneNumber: "",
          facility: null,
        },
        providerVerifyResponse: "",
      };
    },
    computed: {
      facilityIdentifier() {
        if (!this.providerVerify.facility) {
          return "";
        }

        return (
          this.providerVerify.facility.id ||
          this.providerVerify.facility.provider_id ||
          this.providerVerify.facility.id_card_number ||
          this.providerVerify.facility.agency_id ||
          ""
        );
      },
      providerVerifyUssdString() {
        if (!this.providerVerify.enrolleePhoneNumber || !this.facilityIdentifier) {
          return "";
        }

        return `*347*455*6*P${this.providerVerify.enrolleePhoneNumber}/R${this.facilityIdentifier}#`;
      },
      providerVerifyPayloadText() {
        if (!this.providerVerify.enrolleePhoneNumber || !this.facilityIdentifier) {
          return "";
        }

        return `6*P${this.providerVerify.enrolleePhoneNumber}/R${this.facilityIdentifier}`;
      },
      canSubmitProviderVerify() {
        return Boolean(
          this.providerVerify.userPhoneNumber &&
          this.providerVerify.enrolleePhoneNumber &&
          this.providerVerify.facility &&
          this.providerVerifyPayloadText
        );
      },
    },
    mounted() {
      this.getProviders();
    },
    methods: {
      openVerifyCodeForm() {
        this.showVerifyCode = true;
        this.$nextTick(() => {
          if (this.$refs.verifyCodeForm) {
            this.$refs.verifyCodeForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      },
      normalizeNigerianPhone(rawValue) {
        const numeric = String(rawValue || "").replace(/\D/g, "");

        if (!numeric) {
          return "";
        }

        if (numeric.startsWith("234")) {
          return `+${numeric}`;
        }

        if (numeric.startsWith("0")) {
          return `+234${numeric.slice(1)}`;
        }

        return `+234${numeric}`;
      },
      copyUssdString() {
        if (!this.providerVerifyUssdString) {
          return;
        }

        navigator.clipboard.writeText(this.providerVerifyUssdString)
          .then(() => {
            this.$toasted.success("USSD string copied.", {
              position: "top-center",
              duration: 2500,
            });
          })
          .catch(() => {
            this.$toasted.error("Unable to copy USSD string.", {
              position: "top-center",
              duration: 2500,
            });
          });
      },
      getProviders() {
        if (!agency || !agency.id) {
          return;
        }

        this.isFetchingProviders = true;

        this.axios
          .get(`/api/v1/auth/providerAgency/${agency.id}`)
          .then((response) => {
            this.providers = response.data.data || [];
          })
          .catch((error) => {
            this.handleError(error, "Unable to load facilities right now.");
          })
          .finally(() => {
            this.isFetchingProviders = false;
          });
      },
      async submitProviderVerifyCode() {
        this.errorMessage = "";
        this.providerVerifyResponse = "";
        this.isLoading = true;

        try {
          const normalizedPhoneNumber = this.normalizeNigerianPhone(
            this.providerVerify.userPhoneNumber
          );

          if (!normalizedPhoneNumber) {
            this.errorMessage = "Enter a valid user phone number.";
            return;
          }

          this.providerVerify.userPhoneNumber = normalizedPhoneNumber;

          const payload = {
            phoneNumber: normalizedPhoneNumber,
            text: this.providerVerifyPayloadText,
          };

          const response = await this.axios.post(
            "https://api.insucare.ng/api/ussd-session",
            payload,
            {
              headers: {
                Authorization: undefined,
              },
            }
          );

          this.providerVerifyResponse = typeof response.data === "string"
            ? response.data
            : JSON.stringify(response.data, null, 2);

          this.$toasted.success("USSD verification request sent.", {
            position: "top-center",
            duration: 3000,
          });
        } catch (error) {
          this.handleError(error, "Unable to verify code right now.");
        } finally {
          this.isLoading = false;
        }
      },
      handleError(error, fallbackMessage) {
        const serverMessage =
          error &&
          error.response &&
          error.response.data &&
          (error.response.data.message || error.response.data.error || error.response.data);

        this.errorMessage = serverMessage || fallbackMessage;
        this.$toasted.error(this.errorMessage, {
          position: "top-center",
          duration: 3000,
        });
      },
    },
  };
</script>

<style scoped>
  @import url("https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;700&display=swap");

  .self-service-provider-page {
    position: relative;
    min-height: 100vh;
    overflow: hidden;
    font-family: "Space Grotesk", sans-serif;
    background:
      radial-gradient(circle at 10% 15%, rgba(17, 105, 71, 0.15), transparent 45%),
      radial-gradient(circle at 90% 85%, rgba(0, 122, 204, 0.2), transparent 42%),
      linear-gradient(140deg, #f5f8f7 0%, #eef5ff 100%);
  }

  .auth-main {
    position: relative;
    z-index: 1;
    padding: 40px 20px;
  }

  .auth-shell {
    max-width: 1200px;
    margin: 0 auto;
  }

  .bg-shape {
    position: absolute;
    z-index: 0;
    border-radius: 50%;
    filter: blur(2px);
  }

  .bg-shape-one {
    top: -120px;
    right: -120px;
    width: 320px;
    height: 320px;
    background: rgba(0, 122, 204, 0.13);
  }

  .bg-shape-two {
    bottom: -100px;
    left: -100px;
    width: 280px;
    height: 280px;
    background: rgba(17, 105, 71, 0.16);
  }

  .auth-hero {
    text-align: center;
    margin-bottom: 24px;
  }

  .eyebrow {
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #116947;
    font-weight: 700;
    font-size: 0.75rem;
    margin-bottom: 8px;
  }

  .auth-hero h1 {
    margin: 0;
    font-size: clamp(1.7rem, 4vw, 2.4rem);
    line-height: 1.1;
    color: #102a43;
  }

  .subtitle {
    max-width: 620px;
    margin: 12px auto 0;
    color: #334e68;
    font-size: 1rem;
  }

  .auth-card {
    background: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(16, 42, 67, 0.08);
    border-radius: 20px;
    box-shadow: 0 18px 40px rgba(16, 42, 67, 0.12);
    backdrop-filter: blur(6px);
  }

  .section-heading {
    margin-bottom: 24px;
  }

  .section-heading h2 {
    font-size: 1.35rem;
    color: #102a43;
    margin-bottom: 6px;
  }

  .section-heading p {
    margin: 0;
    color: #486581;
  }

  .activity-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
  }

  .activity-card {
    border: 1px solid rgba(16, 42, 67, 0.08);
    border-radius: 18px;
    background: rgba(255, 255, 255, 0.8);
    padding: 20px;
    text-align: left;
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  }

  .activity-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(16, 42, 67, 0.08);
  }

  .activity-card--active {
    border-color: rgba(17, 105, 71, 0.5);
    box-shadow: 0 16px 32px rgba(17, 105, 71, 0.12);
  }

  .activity-card__title {
    display: block;
    font-weight: 700;
    color: #102a43;
    margin-bottom: 8px;
  }

  .activity-card__text {
    display: block;
    color: #486581;
    font-size: 0.95rem;
  }

  .activity-card--request {
    background: linear-gradient(135deg, rgba(244, 173, 66, 0.18), rgba(255, 243, 224, 0.95));
  }

  .activity-card--enrollee {
    background: linear-gradient(135deg, rgba(99, 179, 237, 0.2), rgba(235, 248, 255, 0.95));
  }

  .activity-card--verify {
    background: linear-gradient(135deg, rgba(72, 187, 120, 0.22), rgba(240, 255, 244, 0.95));
  }

  .is-coming-soon {
    cursor: not-allowed;
    opacity: 0.8;
  }

  .activity-card__badge {
    display: inline-block;
    margin-top: 12px;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 4px 8px;
    border-radius: 999px;
    background: rgba(16, 42, 67, 0.12);
    color: #102a43;
  }

  .placeholder-panel,
  .ussd-preview,
  .response-panel {
    border-radius: 16px;
    border: 1px solid rgba(16, 42, 67, 0.08);
    background: rgba(247, 250, 252, 0.92);
    padding: 18px;
    color: #243b53;
  }

  .ussd-preview__label,
  .response-panel__label {
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 10px;
    color: #116947;
    font-weight: 700;
  }

  .ussd-preview code {
    display: block;
    white-space: normal;
    word-break: break-all;
    color: #102a43;
    font-size: 1rem;
  }

  .ussd-guide {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
  }

  .ussd-guide__item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border-radius: 16px;
    background: linear-gradient(120deg, rgba(17, 105, 71, 0.12), rgba(0, 122, 204, 0.1));
    color: #102a43;
  }

  .ussd-guide__item strong {
    font-size: 1rem;
  }

  .response-panel pre {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: inherit;
  }

  .auth-btn {
    border: 0;
    border-radius: 12px;
    background: linear-gradient(120deg, #116947 0%, #007acc 100%);
    color: #fff;
    font-weight: 700;
  }

  .auth-btn:hover {
    color: #fff;
  }

  @media (max-width: 640px) {
    .auth-main {
      padding: 24px 14px;
    }

    .activity-grid,
    .ussd-guide {
      grid-template-columns: 1fr;
    }
  }
</style>
